import moment from 'moment';
import { Label } from 'semantic-ui-react';
import { memo } from 'react';
import { formatCurrency } from '../services';
import { CareHoverTrigger } from './CareHoverCard';
import NotesBillableHeart from './NotesBillableHeart';

/**
 * DateRow — Level-2 expansion row (date level) inside CdpReportsTableByDentists.
 *
 * Props mirror the local variables computed inside getTableRows() for each date
 * iteration within an expanded practice.
 */
const DateRow = ({
    hideColumn,
    row,
    practice,
    showAsterisk,
    date,
    patientVisits,
    DayProcFee,
    dayProcedures,
    dateAdjustment,
    dateWriteOff,
    startEnd,
    practiceDayBCare,
    practiceHynBCare,
    bPCV,
    dayTotalHynCollections,
    prodAdjWo,
    practiceData,
}) => {
    const startEndDisplay = startEnd.length
        ? `${moment(parseInt(startEnd[0].start, 10)).utc().format('hh:mm A')} - ${moment(parseInt(startEnd[0].end, 10)).utc().format('hh:mm A')}`
        : 'Not found';
    const goGaDayType = practiceData[date]?.goGaDayType;
    const goGaDayRate = practiceData[date]?.goGaDayRate;
    const tedShiftName = practiceData[date]?.tedShiftName;

    return (
        <tr>
            {!hideColumn.selectAll && <td className={"expandedLevelOneBackground"}>{''}</td>}
            {!hideColumn.lockStatus && <td className={"expandedLevelOneBackground"}>{''}</td>}
            {!hideColumn.provider && <td className={"expandedLevelOneBackground"}>{''}</td>}
            {!hideColumn.ABBR && <td className={"expandedLevelOneBackground"}>{''}</td>}
            {!hideColumn.practice && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.contractType && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.workTimeType && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.workedDays && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.scheduledDays && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.RTOApproved && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.FTOApproved && <td className={"expandedLevelOneBackground"}></td>}
            {!hideColumn.patientVisits &&
                <td className={"expandedLevelOneBackground"}> {patientVisits}</td>}
            {!hideColumn.bCPV &&
                <td className={"expandedLevelOneBackground"}> {formatCurrency(bPCV)}</td>}
            {!hideColumn.patientVisitsPerDay && <td className={"expandedLevelOneBackground"} />}
            {!hideColumn.rampStartDate && <td className={"expandedLevelOneBackground"} />}
            {!hideColumn.rampEndDate && <td className={"expandedLevelOneBackground"} />}
            {!hideColumn.procedureDate &&
                <td className={"expandedLevelOneBackground"}>
                    {' '}{date}
                    {goGaDayType && (
                        <Label size='mini' className='ml-2' style={goGaDayType === 'GA' ? { color: 'white', backgroundColor: '#E65100' } : undefined}>
                            {goGaDayType}
                        </Label>
                    )}
                    {tedShiftName && (
                        <Label size='mini' className='ml-2' style={{ color: 'white', backgroundColor: '#1565C0' }}>
                            {tedShiftName}
                        </Label>
                    )}
                </td>}
            {!hideColumn.startEnd &&
                <td className={"expandedLevelOneBackground"}> {startEndDisplay}</td>}
            {!hideColumn.care &&
                <td className={"expandedLevelOneBackground"}>
                    <CareHoverTrigger title={date} procedures={dayProcedures} total={DayProcFee}>
                        {formatCurrency(DayProcFee)}
                    </CareHoverTrigger>
                </td>}
            {!hideColumn.writeOffs &&
                <td className={"expandedLevelOneBackground"}> {formatCurrency(dateWriteOff * -1)}</td>}
            {!hideColumn.adjustments &&
                <td className={"expandedLevelOneBackground"}> {formatCurrency(dateAdjustment)}</td>}
            {!hideColumn.careFraction &&
                <td className={"expandedLevelOneBackground"}> {prodAdjWo}%</td>}
            {(!hideColumn.collectionsGoxGozTotal && row.contractType !== 'Collections w/HYN') &&
                <td className={"expandedLevelOneBackground"}>
                    {(row.contractType === 'Collections' || row.contractType === 'Collections Offset')
                        ? formatCurrency(parseFloat(
                            row.data[practice].sumCollectionsTotalByDate?.[`${row.ABBR}_${date}`]?.Amount || 0
                        ))
                        : (row.contractType === 'GOZ' || row.contractType === 'GOX' || row.contractType === 'GOX w/HYN')
                            ? formatCurrency(parseFloat(
                                row.data[practice]?.groupedGoxListByDate?.[date]?.goxNonBillableCare || 0
                            ))
                            : practiceData[date]?.dayTedCollectionsRaw > 0
                                ? formatCurrency(parseFloat(practiceData[date].dayTedCollectionsRaw))
                                : ''}
                </td>}
            {(!hideColumn.collectionsGoxGozTotal && row.contractType === 'Collections w/HYN') &&
                <td className={"expandedLevelOneBackground"}>
                    {formatCurrency(parseFloat(
                        row.data[practice].sumCollectionsTotalByDate?.[`${row.ABBR}_${date}`]?.Amount || 0
                    ))}
                    {showAsterisk && dayTotalHynCollections !== 0
                        ? ' *' + formatCurrency(parseFloat(dayTotalHynCollections)) + '*'
                        : ''}
                </td>}
            {!hideColumn.bCare &&
                <td className={"expandedLevelOneBackground"}>
                    {formatCurrency(practiceDayBCare + practiceData[date].billableNotes)}
                    {showAsterisk && practiceHynBCare !== 0 ? ' *' + formatCurrency(practiceHynBCare) + '*' : ''}
                </td>}
            {hideColumn.totalBasePay ? '' :
                <td className={"expandedLevelOneBackground"}>
                    {goGaDayType
                        ? formatCurrency(goGaDayRate || 0)
                        : row.data[practice] && patientVisits > 0 && row.data[practice].basePayByDay && row.contractType !== 'Prod Hurdle'
                            ? formatCurrency(row.data[practice].basePayByDay || 0)
                            : '-'}
                </td>}
            {!hideColumn.financialNotes &&
                <td className={"expandedLevelOneBackground"}>
                    {formatCurrency((practiceData[date]?.financialNotes) || 0)}
                    <NotesBillableHeart amount={practiceData[date]?.billableNotes} scope="day" />
                </td>}
            {!hideColumn.totalPay &&
                <td className={"expandedLevelOneBackground"}>
                    {formatCurrency((practiceData[date]?.totalPay) || 0)}
                </td>}
            {!hideColumn.normalizedPercentageOfBillableCare &&
                <td className={"expandedLevelOneBackground"}>{''}</td>}
        </tr>
    );
};

// Rows are rebuilt on every table render; skip re-rendering a day whose inputs did not change.
// `startEnd` is a fresh array each render, so it is compared by value.
const sameStartEnd = (a = [], b = []) =>
    a.length === b.length && a.every((s, i) => s?.start === b[i]?.start && s?.end === b[i]?.end);

const areEqual = (prev, next) => {
    const keys = Object.keys(next);
    if (keys.length !== Object.keys(prev).length) return false;
    return keys.every(k => (k === 'startEnd' ? sameStartEnd(prev.startEnd, next.startEnd) : Object.is(prev[k], next[k])));
};

export default memo(DateRow, areEqual);
